import ThemeModal from "components/Common/ThemeModal"
import { Badge, Col, Row, Spinner } from "reactstrap"
import { formatReadableDate } from "utils/formatDate"
import { formatCurrency } from "utils/formatCurrency"
import { getEntityLabel, getReferenceLabel, getUserLabel } from "pages/AccountPages/utils/displayValue"
import ReferenceContextInfo from "pages/AccountPages/utils/ReferenceContextInfo"
import { humanizeReferenceType } from "pages/AccountPages/utils/referenceTypeLabels"

const getMoneyDisplayValue = (value, currency = "BDT") => formatCurrency(value || 0, currency)

const ReceiptRow = ({ label, value, mono = false, strong = false }) => (
  <div className="d-flex align-items-start justify-content-between gap-3 py-2" style={{ borderBottom: "1px dashed rgba(15, 23, 42, 0.12)" }}>
    <span className="text-muted text-uppercase fw-semibold" style={{ fontSize: 10, letterSpacing: 1 }}>{label}</span>
    <span className={`${mono ? "font-monospace" : ""} ${strong ? "fw-bold" : "fw-semibold"} text-dark text-end`.trim()} style={{ fontSize: strong ? 15 : 14 }}>
      {value || "-"}
    </span>
  </div>
)

const ReceiptSection = ({ title, children }) => (
  <div className="pt-3">
    <div className="text-uppercase text-muted fw-semibold mb-2" style={{ fontSize: 11, letterSpacing: 1 }}>{title}</div>
    {children}
  </div>
)

const AccountTransactionView = ({ modal, toggleModal, transaction, loading }) => {
  const accountName = getEntityLabel(transaction?.account, transaction?.account_id, "Account")
  const typeName = getEntityLabel(transaction?.transaction_type || transaction?.transactionType, transaction?.transaction_type_id, "Type")
  const createdBy = getUserLabel(transaction?.createdBy || transaction?.created_by)

  const isCredit = typeName?.toLowerCase?.().includes("credit")
  const badgeColor = isCredit ? "success" : "danger"
  const badgeLabel = isCredit ? "Credit" : "Debit"

  const referenceLabel = getReferenceLabel(transaction?.reference_type, transaction?.reference_id)

  return (
    <ThemeModal isOpen={modal} toggle={toggleModal} title="Transaction Details" size="xl" modalBodyClass="bg-body-tertiary">
      {loading ? (
        <div className="text-center py-4"><Spinner size="sm" /></div>
      ) : !transaction ? (
        <div className="text-center py-4 text-muted">No details available.</div>
      ) : (
        <div className="mx-auto" style={{ maxWidth: 740 }}>
          <div className="rounded-4 border overflow-hidden" style={{ background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)", borderColor: "rgba(15, 23, 42, 0.08)", boxShadow: "0 24px 64px rgba(15, 23, 42, 0.08)" }}>

            <div className="px-4 py-2 d-flex align-items-center justify-content-between" style={{ background: "linear-gradient(90deg, rgba(15, 23, 42, 0.96) 0%, rgba(30, 41, 59, 0.96) 100%)" }}>
              <div className="text-white text-uppercase fw-semibold" style={{ fontSize: 10, letterSpacing: 1.6 }}>Transaction Ledger Entry</div>
              <div className="text-white-50 font-monospace" style={{ fontSize: 12 }}>ATX-{String(transaction.id ?? "0000").padStart(4, "0")}</div>
            </div>

            <div className="px-4 py-3 border-bottom" style={{ background: "radial-gradient(circle at top left, rgba(59, 130, 246, 0.1), transparent 34%), linear-gradient(180deg, #f8fbfd 0%, #ffffff 100%)", borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
                <div>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Account</div>
                  <div className="h5 mb-1 text-dark" style={{ letterSpacing: -0.2 }}>{accountName}</div>
                  <div className="d-flex flex-wrap gap-2">
                    <Badge color={badgeColor} pill className="px-2 py-1">{badgeLabel}</Badge>
                    <Badge color="light" className="text-dark px-2 py-1">{typeName}</Badge>
                    {transaction.is_reversal && <Badge color="secondary" pill className="px-2 py-1">Reversal Entry</Badge>}
                    <span className="text-muted small font-monospace">#{transaction.id ?? "-"}</span>
                    <span className="text-muted small">{transaction.date ? formatReadableDate(transaction.date, "MMM d, y") : "No date"}</span>
                  </div>
                </div>
                <div className="text-md-end">
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Amount</div>
                  <div className={`fw-bold ${isCredit ? "text-success" : "text-danger"}`} style={{ fontSize: 34, lineHeight: 0.95, letterSpacing: -1 }}>
                    {isCredit ? "+" : "-"}{getMoneyDisplayValue(transaction.amount)}
                  </div>
                  <div className="text-muted small mt-1">{typeName}</div>
                </div>
              </div>
            </div>

            <div className="position-relative" style={{ height: 18, background: "#fff" }}>
              <div style={{ position: "absolute", left: 0, right: 0, top: "50%", borderTop: "1px dashed rgba(15, 23, 42, 0.18)" }} />
              <div className="position-absolute rounded-circle bg-body-tertiary" style={{ width: 22, height: 22, left: -11, top: -2 }} />
              <div className="position-absolute rounded-circle bg-body-tertiary" style={{ width: 22, height: 22, right: -11, top: -2 }} />
            </div>

            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <Row className="g-3 g-md-4">
                <Col md={6}>
                  <ReceiptSection title="Transaction Details">
                    <ReceiptRow label="Account" value={accountName} />
                    <ReceiptRow label="Transaction Type" value={typeName} />
                    <ReceiptRow label="Amount" value={getMoneyDisplayValue(transaction.amount)} mono />
                    <ReceiptRow label="Transaction Date" value={transaction.date ? formatReadableDate(transaction.date, "MMM d, y") : "-"} />
                  </ReceiptSection>
                </Col>
                <Col md={6}>
                  <ReceiptSection title="Reference Info">
                    <ReceiptRow label="Reference" value={referenceLabel} mono />
                    <ReceiptRow label="Reference Type" value={humanizeReferenceType(transaction.reference_type) || "None"} />
                    <ReceiptRow label="Reference ID" value={transaction.reference_id ? `#${transaction.reference_id}` : "-"} mono />
                    <ReceiptRow label="Record ID" value={transaction.id ?? "-"} mono />
                    {transaction.is_reversal && (
                      <ReceiptRow label="Reverses Entry" value={transaction.reversed_by ? `#${transaction.reversed_by}` : "Yes"} mono />
                    )}
                  </ReceiptSection>
                </Col>
              </Row>
            </div>

            {transaction.reference_type === "customer_payment" && transaction.reference_context && (
              <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
                <ReceiptSection title="Customer & Source Document">
                  <div className="rounded-3 px-3 py-2" style={{ background: "rgba(248, 250, 252, 0.9)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                    <ReferenceContextInfo referenceType={transaction.reference_type} context={transaction.reference_context} />
                  </div>
                </ReceiptSection>
              </div>
            )}

            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <ReceiptSection title="Ledger Summary">
                <div className="rounded-3 px-3 py-2" style={{ background: "rgba(248, 250, 252, 0.9)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                  <ReceiptRow label="Account" value={accountName} />
                  <ReceiptRow label="Type" value={typeName} />
                  <ReceiptRow label="Reference" value={referenceLabel} mono />
                  <ReceiptRow label="Total Amount" value={getMoneyDisplayValue(transaction.amount)} strong mono />
                </div>
              </ReceiptSection>
            </div>

            {transaction.description && (
              <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
                <ReceiptSection title="Description">
                  <div className="text-dark rounded-3 px-3 py-2" style={{ whiteSpace: "pre-wrap", lineHeight: 1.5, background: "linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, rgba(255, 255, 255, 1) 100%)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                    {transaction.description}
                  </div>
                </ReceiptSection>
              </div>
            )}

            <div className="px-4 py-3" style={{ background: "linear-gradient(180deg, rgba(248, 250, 252, 0.75) 0%, rgba(241, 245, 249, 0.9) 100%)" }}>
              <Row className="g-3">
                <Col md={6}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Created By</div>
                  <div className="small text-dark">{createdBy}</div>
                  <div className="small text-muted">{transaction.created_at ? formatReadableDate(transaction.created_at) : "-"}</div>
                </Col>
                <Col md={6}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Status</div>
                  <div className="small text-dark fw-semibold">{transaction.deleted_at ? "Archived" : "Active"}</div>
                  <div className="small text-muted">{transaction.deleted_at ? formatReadableDate(transaction.deleted_at) : "No deletion recorded"}</div>
                </Col>
              </Row>
            </div>

          </div>
        </div>
      )}
    </ThemeModal>
  )
}

export default AccountTransactionView
